import type { AppointmentView, CatalogItem, ChartData, ChartField, ChartIngredient, ChartItem, ChartOption, ChartView } from "@vaulted/shared";
import { type AiCleanupResult, SIGN_STATEMENTS, fieldOffers, formatDose, isChartTime } from "@vaulted/shared";
import { VITAL_RANGES, type VitalKey } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, errorMessage, get, post, put } from "../api";
import { HealthBlock } from "../components/HealthBlock";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, Panel, TextAreaField, cx, formatDateTime24 } from "../components/ui";
import { useSession } from "../session";
import type { CatalogResponse } from "./Catalog";
import { BuildSheet } from "./Recipe";
import { BackLink } from "../components/BackLink";
import { AgreeBox, SignatureBlock, goToNextReview } from "../components/ESignature";

/**
 * Taking a booked item off the chart means it wasn't given: it comes off the visit and the bill too
 * (through Modify appointment, so the price follows and a paid visit is refused).
 */
const TakeOff = createContext<{ run: (itemId: string, name: string) => void; busy: boolean; error: unknown } | null>(null);

/** The nurse's chart for a visit: tablet-friendly, saved as you go, signed and locked. */
export function ChartPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { can } = useSession();
  const chart = useQuery({ queryKey: ["chart", id], queryFn: () => get<ChartView>(`/api/charts/${id}`) });
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: () => get<CatalogResponse>("/api/catalog") });
  const [data, setData] = useState<ChartData | null>(null);
  const [dirty, setDirty] = useState(false);
  const [signing, setSigning] = useState(false);
  // Signing starts with a look at the chart as it will be (the finished layout), then Sign at the bottom.
  const [previewing, setPreviewing] = useState(false);
  const flip = (on: boolean) => {
    setPreviewing(on);
    window.scrollTo({ top: 0 });
  };
  const loadedFor = useRef<string | null>(null);
  if (chart.data && loadedFor.current !== chart.data.id) {
    loadedFor.current = chart.data.id;
    setData(chart.data.data);
  }
  const c = chart.data;
  const editable = !!c && c.status === "draft" && can("charts.write");

  const save = useMutation({
    mutationFn: (d: ChartData) => put<ChartView>(`/api/charts/${id}`, { data: d, baseUpdatedAt: chart.data?.updatedAt }),
    onSuccess: (v) => {
      qc.setQueryData(["chart", id], v);
      // Keep what's on screen (typing continues while saving); take the server's "added" marks.
      setData((cur) => (cur ? mergeAdded(cur, v.data) : v.data));
    },
    onError: async (e) => {
      // The appointment was modified: show the chart as it is now.
      if (e instanceof ApiError && e.code === "chart_changed") {
        loadedFor.current = null;
        await qc.invalidateQueries({ queryKey: ["chart", id] });
      }
    },
  });
  // Save a moment after the last change.
  useEffect(() => {
    if (!dirty || !data || !editable) return;
    const t = setTimeout(() => {
      setDirty(false);
      save.mutate(data);
    }, 1200);
    return () => clearTimeout(t);
  }, [data, dirty, editable]); // eslint-disable-line react-hooks/exhaustive-deps

  const takeOff = useMutation({
    mutationFn: async (itemId: string) => {
      // Save what's typed first, so nothing is lost when the chart reloads.
      if (dirty && data) {
        setDirty(false);
        await save.mutateAsync(data);
      }
      const a = await get<AppointmentView>(`/api/appointments/${chart.data!.appointmentId}`);
      if (a.services.some((x) => x.serviceId === itemId)) throw new Error("To change the service itself, use Modify appointment on the visit.");
      await post(`/api/appointments/${a.id}/edit`, {
        services: a.services.map((x) => ({ serviceId: x.serviceId, addOnIds: x.addOns.map((y) => y.id).filter((id) => id !== itemId) })),
        staffId: a.staffId,
        start: a.start,
        priceCents: a.priceOverrideReason ? a.priceCents : null,
        priceReason: "",
        servicesReason: "",
        forceReason: null,
      });
    },
    onSuccess: async () => {
      loadedFor.current = null;
      await qc.invalidateQueries({ queryKey: ["chart", id] });
      await qc.invalidateQueries({ queryKey: ["appointment", chart.data?.appointmentId] });
      await qc.invalidateQueries({ queryKey: ["calendar"] });
    },
  });
  const askTakeOff = (itemId: string, name: string) => window.confirm(`${name} wasn't given? It comes off the visit and the bill.`) && takeOff.mutate(itemId);

  const adverse = useMutation({
    mutationFn: () => post<ChartView>(`/api/appointments/${c!.appointmentId}/charts`, { templateKey: "adverse" }),
    onSuccess: (v) => nav(`/charts/${v.id}`),
  });

  if (chart.error) return <Alert>{errorMessage(chart.error)}</Alert>;
  if (!c || !data) return <Loading />;
  const change = (key: string, v: ChartData[string]) => {
    setData({ ...data, [key]: v });
    setDirty(true);
  };

  // Build sheet: the checked service, with checked add-ons and medications.
  const checked = Object.values(data).flatMap((v) => (Array.isArray(v) && v.length && typeof v[0] === "object" ? (v as ChartItem[]) : []));
  const items = catalog.data?.items ?? [];
  const main = checked.map((i) => items.find((x) => x.id === i.catalogItemId)).find((x): x is CatalogItem => !!x && x.type === "service");
  const extras = checked.map((i) => i.catalogItemId).filter((x): x is string => !!x && x !== main?.id);

  return (
    <TakeOff.Provider value={{ run: askTakeOff, busy: takeOff.isPending, error: takeOff.error }}>
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap gap-x-4 text-sm">
            <BackLink to={`/visits/${c.appointmentId}`} label="Visit" className="font-semibold text-brand hover:underline" />
            <Link to={`/clients/${c.clientId}`} className="text-muted hover:underline">
              {c.record.client.name}'s profile
            </Link>
          </div>
          <h1 className="page-title">{c.template.name}</h1>
          <p className="text-ink-soft">
            {c.record.visit.services.join(" + ")} · {c.record.visit.date} {c.record.visit.start.slice(11, 16)}
            {c.record.visit.nurse && ` · ${c.record.visit.nurse}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {c.replacedBy?.status === "signed" ? <Badge tone="off">Replaced</Badge> : c.status === "signed" ? <Badge tone="ok">Signed</Badge> : c.status === "discarded" ? <Badge tone="off">Discarded</Badge> : <Badge tone="wait">{c.replaces ? "Update, not signed yet" : "Draft"}</Badge>}
          {editable && <span className="text-sm text-muted">{save.isPending ? "Saving…" : dirty ? "Editing" : save.isSuccess ? "Saved" : ""}</span>}
        </div>
      </div>
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {takeOff.error && <Alert>{errorMessage(takeOff.error)}</Alert>}
      <UpdateBanner c={c} canWrite={can("charts.write")} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex flex-col gap-4">
          {editable && previewing && (
            <div className="flex items-center gap-2 rounded-[var(--radius-panel)] bg-wait-wash px-2 py-1.5 text-sm print:hidden">
              <Button variant="quiet" className="shrink-0" onClick={() => flip(false)}>
                ← Back
              </Button>
              <span className="min-w-0 flex-1 text-center">
                <span className="block font-semibold text-wait">Preview, not signed yet</span>
                <span className="block text-xs text-ink-soft">Check it over, then sign at the bottom.</span>
              </span>
              <Button variant="quiet" className="shrink-0" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          )}
          {editable && !previewing ? (
            c.template.sections.map((s) => (
              <Panel key={s.title} className="p-5">
                <h2 className="mb-3 text-base font-semibold">{s.title}</h2>
                <SectionFields fields={s.fields} c={c} data={data} onChange={change} />
              </Panel>
            ))
          ) : (
            <ChartDocument c={c} data={data} />
          )}
          {/* At the bottom: the nurse signs after working down the chart. */}
          {(editable || (can("charts.write") && c.templateKey !== "adverse")) && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {adverse.error && (
                <div className="basis-full">
                  <Alert>{errorMessage(adverse.error)}</Alert>
                </div>
              )}
              {can("charts.write") && c.templateKey !== "adverse" && !previewing && (
                <Button variant="quiet" busy={adverse.isPending} onClick={() => adverse.mutate()}>
                  Adverse reaction report
                </Button>
              )}
              {editable && previewing && (
                <Button variant="quiet" onClick={() => flip(false)}>
                  ← Back to editing
                </Button>
              )}
              {editable && (
                <Button variant="primary" onClick={() => (previewing ? setSigning(true) : flip(true))}>
                  {previewing ? "Sign chart" : "Preview chart"}
                </Button>
              )}
            </div>
          )}
          {c.status === "signed" && <Addenda c={c} canWrite={can("charts.write")} />}
        </div>
        <aside className="flex flex-col gap-4">
          {c.templateKey !== "adverse" && main && catalog.data && <BuildSheet item={main} data={catalog.data} extraIds={extras} lotStrengths={lotStrengths(c, checked)} />}
          {c.status === "signed" && (
            <Panel className="flex flex-col gap-3 p-4 text-sm print:hidden">
              <p className="text-ink-soft">Signed charts can't be changed: add an addendum for a note, or update the chart to change what was given.</p>
              <Button variant="secondary" className="self-start" onClick={() => window.print()}>
                Print
              </Button>
            </Panel>
          )}
          {c.status === "signed" && c.review.status === "waiting" && <ReviewPanel c={c} canReview={can("charts.sign")} />}
        </aside>
      </div>
      {signing && <SignDialog c={c} data={data} onClose={() => setSigning(false)} />}
    </div>
    </TakeOff.Provider>
  );
}

/**
 * Updating a signed chart: why this one was updated (and what it replaces), that it was replaced,
 * or, on the visit's current signed chart, what was billed at checkout that it doesn't have.
 */
function UpdateBanner({ c, canWrite }: { c: ChartView; canWrite: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const canUpdate = canWrite && c.status === "signed" && c.templateKey !== "adverse" && !c.replacedBy;
  // Arrived from "Update chart" (the visit, the pop-up, Pending Charts): the reason box opens straight away.
  const [search] = useSearchParams();
  const [asking, setAsking] = useState(() => canUpdate && search.get("update") === "1");
  const [reason, setReason] = useState("");
  const start = useMutation({
    mutationFn: () => post<ChartView>(`/api/charts/${c.id}/update`, { reason }),
    onSuccess: async (v) => {
      await qc.invalidateQueries({ queryKey: ["chart", c.id] });
      await qc.invalidateQueries({ queryKey: ["charts-pending"] });
      setAsking(false);
      nav(`/charts/${v.id}`);
    },
  });
  const discard = useMutation({
    mutationFn: () => post<{ chartId: string }>(`/api/charts/${c.id}/discard`, {}),
    onSuccess: async (v) => {
      await qc.invalidateQueries({ queryKey: ["chart"] });
      await qc.invalidateQueries({ queryKey: ["charts-pending"] });
      nav(`/charts/${v.chartId}`);
    },
  });
  return (
    <>
      {c.replaces && (
        <Alert tone="info">
          <p>
            <span className="font-semibold">Updated chart.</span> It takes the place of the chart signed by {c.replaces.signedBy} on {formatDateTime24(c.replaces.signedAt)}, which stays in the file unchanged.{" "}
            <Link to={`/charts/${c.replaces.id}`} className="font-semibold text-brand hover:underline">
              Open the earlier chart
            </Link>
          </p>
          {c.updateReason && <p className="mt-1">Why: {c.updateReason}</p>}
          {c.status === "draft" && canWrite && (
            <div className="mt-2">
              {discard.error && <Alert>{errorMessage(discard.error)}</Alert>}
              <Button variant="quiet" busy={discard.isPending} onClick={() => window.confirm("Discard this update? The signed chart stays as it is.") && discard.mutate()}>
                Discard this update
              </Button>
            </div>
          )}
        </Alert>
      )}
      {c.replacedBy && (
        <Alert tone="info">
          {c.replacedBy.status === "signed" ? (
            <>
              Replaced by the updated chart, {formatDateTime24(c.replacedBy.at)} ({c.replacedBy.by}).{" "}
            </>
          ) : (
            <>An update to this chart was started by {c.replacedBy.by} and isn't signed yet. </>
          )}
          <Link to={`/charts/${c.replacedBy.id}`} className="font-semibold text-brand hover:underline">
            Open the updated chart
          </Link>
        </Alert>
      )}
      {c.status === "signed" && c.notCharted.length > 0 && !c.replacedBy && (
        <Alert>
          <span className="font-semibold">Chart needs updating:</span> {c.notCharted.join(", ")} {c.notCharted.length === 1 ? "was" : "were"} added at checkout but {c.notCharted.length === 1 ? "isn't" : "aren't"} on this chart.
        </Alert>
      )}
      {canUpdate && (
        <div className="flex justify-end">
          <Button variant={c.notCharted.length ? "primary" : "quiet"} onClick={() => setAsking(true)}>
            Update chart
          </Button>
        </div>
      )}
      {asking && (
        <Dialog
          title="Update this chart?"
          onClose={() => setAsking(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setAsking(false)}>
                Back
              </Button>
              <Button variant="primary" busy={start.isPending} disabled={reason.trim().length < 3} onClick={() => start.mutate()}>
                Start the update
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p>
              A new chart opens with everything from this one{c.notCharted.length ? `, plus ${c.notCharted.join(", ")} from checkout` : ""}. Change what you need and sign it. This chart stays in the file, unchanged, marked as replaced.
            </p>
            <TextAreaField label="Why is the chart being updated?" placeholder="Client asked for Zofran after the drip" value={reason} onChange={(e) => setReason(e.target.value)} />
            {start.error && <Alert>{errorMessage(start.error)}</Alert>}
          </div>
        </Dialog>
      )}
    </>
  );
}

/** Strength of the lot picked for each medication (first pick wins). */
function lotStrengths(c: ChartView, items: ChartItem[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const g of items.flatMap((i) => i.ingredients ?? [])) {
    const s = g.lotId ? (c.lots[g.formularyItemId] ?? []).find((l) => l.id === g.lotId)?.strengthPerMl : null;
    if (s && !(g.formularyItemId in out)) out[g.formularyItemId] = s;
  }
  return out;
}

/** A new chart line: its medications, each to be given a lot. */
export const newChartItem = (o: ChartOption, lots: ChartView["lots"] = {}): ChartItem => ({
  catalogItemId: o.catalogItemId,
  name: o.name,
  lot: "",
  expiration: "",
  added: !o.booked,
  ...(o.ingredients?.length
    ? {
        ingredients: o.ingredients.map((g) => {
          // Start on the usable lot that expires first (soonest first, as listed).
          const one = (lots[g.formularyItemId] ?? []).find((l) => l.status !== "expired") ?? null;
          return { formularyItemId: g.formularyItemId, name: g.name, dose: g.dose, unit: g.unit, lotId: one?.id ?? null, lot: one?.lotNumber ?? "", expiration: one?.expiresOn ?? "" };
        }),
      }
    : {}),
});

/** Keep local edits but take the server's "added during the visit" marks. */
function mergeAdded(local: ChartData, server: ChartData): ChartData {
  const out = { ...local };
  for (const [k, v] of Object.entries(server)) {
    const mine = out[k];
    if (Array.isArray(v) && Array.isArray(mine) && v.length === mine.length && typeof v[0] === "object") {
      out[k] = (mine as ChartItem[]).map((x, i) => ({ ...x, added: (v as ChartItem[])[i]!.added }));
    }
  }
  return out;
}

export function FieldView({ f, c, data, editable, onChange }: { f: ChartField; c: ChartView; data: ChartData; editable: boolean; onChange: (key: string, v: ChartData[string]) => void }) {
  switch (f.kind) {
    case "record":
      return <RecordBlock which={f.key} c={c} />;
    case "checks": {
      const picked = (data[f.key] as string[] | undefined) ?? [];
      return (
        <fieldset>
          <legend className="mb-1.5 text-sm font-semibold text-ink-soft">
            {f.label}
            {f.required && <span className="text-danger"> *</span>}
          </legend>
          <div className="flex flex-wrap gap-2">
            {f.options.map((o) => (
              <Chip key={o} on={picked.includes(o)} disabled={!editable} onClick={() => onChange(f.key, picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o])}>
                {o}
              </Chip>
            ))}
          </div>
        </fieldset>
      );
    }
    case "choice": {
      const v = (data[f.key] as string | undefined) ?? "";
      const isOther = !!v && !f.options.includes(v);
      return (
        <fieldset>
          <legend className="mb-1.5 text-sm font-semibold text-ink-soft">
            {f.label}
            {f.required && <span className="text-danger"> *</span>}
          </legend>
          <div className="flex flex-wrap items-center gap-2">
            {f.options.map((o) => (
              <Chip key={o} on={v === o} disabled={!editable} onClick={() => onChange(f.key, v === o ? "" : o)}>
                {o}
              </Chip>
            ))}
            {f.other && (editable || isOther) && (
              <input
                className="w-28 rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-sm disabled:bg-mist"
                placeholder="Other"
                disabled={!editable}
                value={isOther ? v : ""}
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
          </div>
        </fieldset>
      );
    }
    case "input": {
      const v = (data[f.key] as string | undefined) ?? "";
      const label = f.required ? `${f.label} *` : f.label;
      if (f.input === "time") return <TimeField24 label={label} disabled={!editable} value={v} onChange={(x) => onChange(f.key, x)} />;
      if (f.multiline && f.aiCleanup)
        return (
          <div className="flex flex-col gap-2">
            <TextAreaField label={label} rows={5} disabled={!editable} value={v} onChange={(e) => onChange(f.key, e.target.value)} />
            {editable && <AiCleanup chartId={c.id} field={f.key} text={v} data={data} onUse={(x) => onChange(f.key, x)} />}
          </div>
        );
      return f.multiline ? (
        <TextAreaField label={label} disabled={!editable} value={v} onChange={(e) => onChange(f.key, e.target.value)} />
      ) : (
        <Field label={label} type={f.input ?? "text"} inputMode={f.input === "number" ? "decimal" : undefined} disabled={!editable} value={v} onChange={(e) => onChange(f.key, e.target.value)} />
      );
    }
    case "vitals":
      return <VitalsRow f={f} v={(data[f.key] as Record<string, string> | undefined) ?? {}} editable={editable} onChange={(v) => onChange(f.key, v)} />;
    case "catalog":
      return <CatalogGroup fields={[f]} c={c} data={data} editable={editable} onChange={onChange} />;
  }
}

function Chip({ on, disabled, onClick, children }: { on: boolean; disabled: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        "min-h-10 rounded-full border px-3.5 py-1.5 text-left text-sm font-medium transition-colors",
        on ? "border-brand bg-brand text-white" : "border-line bg-white text-ink hover:border-ink-soft",
        disabled && (on ? "opacity-90" : "opacity-50"),
      )}
    >
      {on && "✓ "}
      {children}
    </button>
  );
}

/** Why a line is on the chart without being booked. */
function AddedNote({ x }: { x: ChartItem }) {
  if (!x.added) return null;
  return <span className="text-xs font-semibold text-wait">{x.atCheckout ? "Added at checkout (already on the bill)" : "Added during the visit (goes on the bill)"}</span>;
}

type CatalogChartField = Extract<ChartField, { kind: "catalog" }>;
const ADD_LABELS: Record<string, string> = { cocktail: "Add a drip", addons: "Add to the bag", injections: "Add an injection", medications: "Add a medication or vitamin", recovery: "Add recovery" };

/**
 * A chart's "Given" lists while charting: each item a small table (medication, dose, lot ·
 * expiration), then one row of "+ Add…" boxes. Empty lists show nothing.
 */
function CatalogGroup({ fields, c, data, editable, onChange }: { fields: CatalogChartField[]; c: ChartView; data: ChartData; editable: boolean; onChange: (key: string, v: ChartData[string]) => void }) {
  const takeOff = useContext(TakeOff);
  const listOf = (f: CatalogChartField) => (data[f.key] as ChartItem[] | undefined) ?? [];
  const total = fields.reduce((n, f) => n + listOf(f).length, 0);
  return (
    <div className="flex flex-col gap-3">
      {fields.flatMap((f) => {
        const list = listOf(f);
        const set = (i: number, patch: Partial<ChartItem>) => onChange(f.key, list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
        // A booked item (not one added during the visit) comes off the visit too; anything else just leaves the chart.
        const remove = (i: number) => {
          const x = list[i]!;
          const booked = !x.added && c.options.some((o) => o.catalogItemId === x.catalogItemId && o.booked);
          if (booked && takeOff && x.catalogItemId) takeOff.run(x.catalogItemId, x.name);
          else onChange(f.key, list.filter((_, j) => j !== i));
        };
        const recovery = f.sections.every((sec) => sec === "recovery");
        return list.map((x, i) => (
          <div key={`${f.key}-${x.catalogItemId}-${i}`} className="rounded-xl border border-line px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-bold">{x.name}</span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted">{f.label}</span>
                <AddedNote x={x} />
              </div>
              {editable && (
                <button type="button" disabled={takeOff?.busy} className="text-sm font-semibold text-muted hover:text-danger" onClick={() => remove(i)}>
                  Remove
                </button>
              )}
            </div>
            {recovery ? (
              <p className="mt-1 text-sm text-muted">Recovery · no lot needed</p>
            ) : (
              <table className="mt-2 w-full table-fixed text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted">
                    <th className="w-[42%] pb-1.5 font-bold">Medication</th>
                    <th className="w-[18%] pb-1.5 font-bold">Dose</th>
                    <th className="pb-1.5 font-bold">Lot · Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {x.ingredients?.length ? (
                    x.ingredients.map((g, k) => (
                      <IngredientRow key={g.formularyItemId} g={g} c={c} editable={editable} onChange={(patch) => set(i, { ingredients: x.ingredients!.map((y, n) => (n === k ? { ...y, ...patch } : y)) })} />
                    ))
                  ) : (
                    // An item with no medications in its recipe: one vial, its lot typed.
                    <tr className="border-t border-mist">
                      <td className="py-1.5 pr-2">{x.name}</td>
                      <td className="py-1.5 pr-2 text-muted">—</td>
                      <td className="py-1.5">
                        <div className="flex gap-2">
                          <input aria-label={`Lot for ${x.name}`} placeholder="Lot" disabled={!editable} className="w-28 rounded-[var(--radius-control)] border border-line px-2 py-1.5" value={x.lot} onChange={(e) => set(i, { lot: e.target.value })} />
                          <input aria-label={`Expiration for ${x.name}`} type="month" disabled={!editable} className="rounded-[var(--radius-control)] border border-line px-2 py-1.5" value={x.expiration.slice(0, 7)} onChange={(e) => set(i, { expiration: e.target.value })} />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        ));
      })}
      {!total && <p className="text-sm text-muted">Nothing charted yet.</p>}
      {editable && (
        <div className="flex flex-wrap gap-2">
          {fields.map((f) => {
            const list = listOf(f);
            const options = c.options.filter((o) => fieldOffers(f, o) && !list.some((x) => x.catalogItemId === o.catalogItemId)).sort((a, b) => a.name.localeCompare(b.name));
            if (!options.length || (f.single && list.length)) return null;
            return (
              <select
                key={f.key}
                aria-label={ADD_LABELS[f.key] ?? `Add to ${f.label}`}
                className="rounded-[var(--radius-control)] border border-dashed border-line bg-white px-3 py-2 text-sm text-ink-soft"
                value=""
                onChange={(e) => {
                  const o = c.options.find((x) => x.catalogItemId === e.target.value);
                  if (o) onChange(f.key, [...list, newChartItem(o, c.lots)]);
                }}
              >
                <option value="">+ {ADD_LABELS[f.key] ?? `Add to ${f.label.toLowerCase()}`}…</option>
                {options.map((o) => (
                  <option key={o.catalogItemId} value={o.catalogItemId}>
                    {o.name}
                    {o.booked ? " (booked)" : ""}
                  </option>
                ))}
              </select>
            );
          })}
        </div>
      )}
    </div>
  );
}

const TYPED = "__typed";
const fmtExp = (d: string) => (d.length === 10 ? new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: "2-digit", day: "2-digit", year: "2-digit", timeZone: "UTC" }) : d);
const doseText = (g: ChartIngredient) => (g.unit === "ml" ? formatDose(g.dose, "ml") : g.unit === "each" ? (g.dose === 1 ? "1 bag" : `${g.dose} bags`) : formatDose(g.dose, g.unit));

/** One medication's (or bag's) line: its lot, picked from inventory (open vials, then soonest) or typed for a vial the app doesn't know. */
function IngredientRow({ g, c, editable, onChange }: { g: ChartIngredient; c: ChartView; editable: boolean; onChange: (p: Partial<ChartIngredient>) => void }) {
  const lots = c.lots[g.formularyItemId] ?? [];
  const [typing, setTyping] = useState(!g.lotId && !!g.lot);
  const label = (l: (typeof lots)[number]) => `${l.lotNumber || "No lot #"}${l.expiresOn ? ` · ${fmtExp(l.expiresOn)}` : ""}${l.status === "expiring" ? " · expiring soon" : ""}${l.open ? " · open" : ""}`;
  const lotCell = !editable ? (
    <span className={g.lot ? "" : "text-muted"}>{g.lot || g.expiration ? `${g.lot || "—"}${g.expiration ? ` · ${fmtExp(g.expiration)}` : ""}` : "No lot recorded"}</span>
  ) : typing ? (
    <div className="flex flex-wrap items-center gap-2">
      <input aria-label={`Lot for ${g.name}`} placeholder="Lot" className="w-28 rounded-[var(--radius-control)] border border-line px-2 py-1.5" value={g.lot} onChange={(e) => onChange({ lot: e.target.value, lotId: null })} />
      <input aria-label={`Expiration for ${g.name}`} type="month" className="rounded-[var(--radius-control)] border border-line px-2 py-1.5" value={g.expiration.slice(0, 7)} onChange={(e) => onChange({ expiration: e.target.value, lotId: null })} />
      {lots.length > 0 && (
        <button
          type="button"
          className="text-xs font-bold text-brand"
          onClick={() => {
            setTyping(false);
            onChange({ lot: "", expiration: "" });
          }}
        >
          Pick from shelf
        </button>
      )}
    </div>
  ) : (
    <select
      aria-label={`Lot for ${g.name}`}
      className={cx("w-full rounded-[var(--radius-control)] border bg-white px-2 py-1.5", g.lotId ? "border-line" : "border-wait text-wait")}
      value={g.lotId ?? ""}
      onChange={(e) => {
        if (e.target.value === TYPED) {
          setTyping(true);
          onChange({ lotId: null, lot: "", expiration: "" });
          return;
        }
        const l = lots.find((x) => x.id === e.target.value);
        onChange(l ? { lotId: l.id, lot: l.lotNumber, expiration: l.expiresOn ?? "" } : { lotId: null, lot: "", expiration: "" });
      }}
    >
      <option value="">{lots.length ? "Pick the lot…" : "None on the shelf"}</option>
      {lots.map((l) => (
        <option key={l.id} value={l.id}>
          {label(l)}
        </option>
      ))}
      <option value={TYPED}>Type it (not on the shelf)…</option>
    </select>
  );
  return (
    <tr className="border-t border-mist align-middle">
      <td className="py-1.5 pr-2">{g.name}</td>
      <td className="py-1.5 pr-2 tabular-nums">{doseText(g)}</td>
      <td className="py-1.5">{lotCell}</td>
    </tr>
  );
}

function RecordBlock({ which, c }: { which: "client" | "visit" | "health" | "forms"; c: ChartView }) {
  const r = c.record;
  if (which === "client")
    return (
      <p>
        <span className="font-semibold">{r.client.name}</span> · DOB {r.client.dob} (age {r.client.age})
      </p>
    );
  if (which === "visit")
    return (
      <p>
        {r.visit.date} {r.visit.start.slice(11, 16)} · {r.visit.services.join(" + ")} · Nurse: {r.visit.nurse || "—"}
      </p>
    );
  if (which === "health") return <HealthBlock h={r.health} />;
  return (
    <div className="flex flex-wrap gap-2 text-sm">
      {r.forms.map((f) => (
        <Badge key={f.name} tone={f.ok ? "ok" : "wait"}>
          {f.ok ? "✓" : "Missing:"} {f.name}
        </Badge>
      ))}
    </div>
  );
}

function SignDialog({ c, data, onClose }: { c: ChartView; data: ChartData; onClose: () => void }) {
  const qc = useQueryClient();
  const takeOff = useContext(TakeOff);
  // Booked items the chart doesn't have (signing refuses until they're charted or taken off).
  const charted = new Set(Object.values(data).flatMap((x) => (Array.isArray(x) && x.length && typeof x[0] === "object" ? (x as ChartItem[]) : [])).map((i) => i.catalogItemId));
  const catalogFields = c.template.sections.flatMap((s) => s.fields).filter((f): f is Extract<ChartField, { kind: "catalog" }> => f.kind === "catalog");
  const notCharted = c.options.filter((o) => o.booked && !charted.has(o.catalogItemId) && catalogFields.some((f) => fieldOffers(f, o)));
  const nav = useNavigate();
  const [complete, setComplete] = useState(c.templateKey !== "adverse");
  const [agree, setAgree] = useState(false);
  const sign = useMutation({
    mutationFn: () => post<ChartView>(`/api/charts/${c.id}/sign`, { data, complete, baseUpdatedAt: c.updatedAt, agree }),
    onSuccess: async (v) => {
      qc.setQueryData(["chart", c.id], v);
      await qc.invalidateQueries({ queryKey: ["calendar"] });
      await qc.invalidateQueries({ queryKey: ["appointment", c.appointmentId] });
      onClose();
      // Signed: back to the visit (the signed chart opens from there).
      nav(`/visits/${c.appointmentId}`);
    },
  });
  return (
    <Dialog
      title="Sign this chart?"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Back
          </Button>
          <Button variant="primary" busy={sign.isPending} disabled={!agree} onClick={() => sign.mutate()}>
            Sign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p>A signed chart is locked; corrections are added as addenda.</p>
        {c.replaces && <p>This updated chart takes the place of the one signed by {c.replaces.signedBy} on {formatDateTime24(c.replaces.signedAt)}, which stays in the file. Only what changed comes off inventory.</p>}
        {notCharted.length > 0 && (
          <Alert tone="info">
            Booked but not on the chart: chart {notCharted.length === 1 ? "it" : "them"} if given.
            <ul className="mt-2 flex flex-col gap-1">
              {notCharted.map((o) => (
                <li key={o.catalogItemId} className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{o.name}</span>
                  {takeOff && (
                    <Button variant="quiet" busy={takeOff.busy} onClick={() => takeOff.run(o.catalogItemId, o.name)}>
                      Not given: take it off the visit
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {c.templateKey !== "adverse" && <Checkbox label="Complete the visit too" checked={complete} onChange={setComplete} />}
        <AgreeBox statement={SIGN_STATEMENTS.chart} checked={agree} onChange={setAgree} />
        {sign.error && <Alert>{errorMessage(sign.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function Addenda({ c, canWrite }: { c: ChartView; canWrite: boolean }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: () => post<ChartView>(`/api/charts/${c.id}/addenda`, { text }),
    onSuccess: (v) => {
      qc.setQueryData(["chart", c.id], v);
      setText("");
    },
  });
  return (
    <Panel className="p-5">
      <h2 className="mb-3 text-base font-semibold">Addenda</h2>
      <ul className="flex flex-col gap-3">
        {c.addenda.map((a) => (
          <li key={a.id} className="border-l-4 border-line pl-3">
            <p className="whitespace-pre-wrap">{a.text}</p>
            <p className="text-xs text-muted">
              {a.by}, {formatDateTime24(a.at)}
            </p>
          </li>
        ))}
        {!c.addenda.length && <li className="text-sm text-muted">None</li>}
      </ul>
      {canWrite && (
        <form
          className="mt-4 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <TextAreaField label="Add an addendum" value={text} onChange={(e) => setText(e.target.value)} />
          {add.error && <Alert>{errorMessage(add.error)}</Alert>}
          <Button type="submit" className="self-start" busy={add.isPending} disabled={!text.trim()}>
            Add and sign
          </Button>
        </form>
      )}
    </Panel>
  );
}

/** Medical director review: waiting (with the button for the director) or done. */
function ReviewPanel({ c, canReview }: { c: ChartView; canReview: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [note, setNote] = useState("");
  const [agree, setAgree] = useState(false);
  const review = useMutation({
    mutationFn: () => post<ChartView>(`/api/charts/${c.id}/review`, { note, agree }),
    onSuccess: async (v) => {
      qc.setQueryData(["chart", c.id], v);
      await qc.invalidateQueries({ queryKey: ["charts-pending-review"] });
      await qc.invalidateQueries({ queryKey: ["client-charts"] });
      // On to the next chart waiting for review.
      await goToNextReview(qc, nav, c.id);
    },
  });
  const r = c.review;
  if (r.status === "reviewed") {
    return (
      <Panel className="flex flex-col gap-2 p-4 text-sm">
        <p className="font-semibold text-ok">Reviewed by the medical director</p>
        {r.signature ? (
          <SignatureBlock title="Signed by" sig={r.signature} />
        ) : (
          <p>
            {r.by}, {formatDateTime24(r.at)}
          </p>
        )}
        {r.note && <p className="whitespace-pre-wrap text-ink-soft">{r.note}</p>}
      </Panel>
    );
  }
  return (
    <Panel className="flex flex-col gap-3 p-4 text-sm">
      <p className="font-semibold text-wait">Waiting for medical director review</p>
      {canReview && (
        <>
          <TextAreaField label="Note (optional)" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
          <AgreeBox statement={SIGN_STATEMENTS.review} checked={agree} onChange={setAgree} />
          {review.error && <Alert>{errorMessage(review.error)}</Alert>}
          <Button variant="primary" busy={review.isPending} disabled={!agree} onClick={() => review.mutate()}>
            Sign review and go to the next
          </Button>
        </>
      )}
    </Panel>
  );
}

/**
 * "AI Cleanup": the narrative (only) goes to the AI and comes back tidied, as a suggestion under it.
 * Left empty, it's "AI Draft from chart": a narrative written from what's filled in on the chart.
 * Nothing changes until "Use this"; numbers that weren't in the original (or on the chart) are pointed out first.
 */
function AiCleanup({ chartId, field, text, data, onUse }: { chartId: string; field: string; text: string; data: ChartData; onUse: (t: string) => void }) {
  const [result, setResult] = useState<AiCleanupResult | null>(null);
  const empty = !text.trim();
  const run = useMutation({
    mutationFn: () => post<AiCleanupResult>(`/api/charts/${chartId}/ai-cleanup`, empty ? { field, text: "", data } : { field, text }),
    onSuccess: setResult,
  });
  return (
    <div className="flex flex-col gap-2">
      <Button type="button" variant="secondary" className="self-start" busy={run.isPending} onClick={() => run.mutate()}>
        {empty ? "AI Draft from chart" : "AI Cleanup"}
      </Button>
      {run.error && <Alert>{errorMessage(run.error)}</Alert>}
      {result && (
        <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line bg-mist/40 p-3 text-sm">
          <p className="font-semibold">{result.drafted ? "Drafted from the chart (check it matches the visit, and add anything only you know):" : "Cleaned up (check it says the same as yours):"}</p>
          <p className="whitespace-pre-wrap">{result.text}</p>
          {result.newNumbers.length > 0 && (
            <Alert>
              Check this: {result.newNumbers.join(", ")} {result.newNumbers.length === 1 ? "isn't" : "aren't"} in {result.drafted ? "the chart" : "what you wrote"}.
            </Alert>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                onUse(result.text);
                setResult(null);
              }}
            >
              Use this
            </Button>
            <Button type="button" variant="quiet" onClick={() => setResult(null)}>
              Keep mine
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Chart times are 24-hour ("14:35"). The browser's time picker follows the device's clock setting
 * (AM/PM on most iPads), so this is a plain field: digits get their colon, and Now fills it in.
 */
function TimeField24({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (v: string) => void }) {
  const tidy = (raw: string) => {
    const d = raw.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
  };
  // "930" → "09:30" when they move on.
  const pad = () => {
    const d = value.replace(/\D/g, "");
    if (d.length === 3) onChange(`0${d[0]}:${d.slice(1)}`);
  };
  const now = () => {
    const t = new Date();
    onChange(`${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`);
  };
  const bad = value.length === 5 && !isChartTime(value);
  return (
    <div className="relative">
      <Field label={label} placeholder="HH:MM (24-hour)" inputMode="numeric" maxLength={5} disabled={disabled} value={value} onChange={(e) => onChange(tidy(e.target.value))} onBlur={pad} hint={bad ? "Use 24-hour time, 00:00 to 23:59." : undefined} />
      {!disabled && (
        <button type="button" onClick={now} className="absolute right-2 top-[30px] rounded px-1.5 py-1 text-xs font-bold text-brand hover:bg-mist">
          Now
        </button>
      )}
    </div>
  );
}

/**
 * A section while charting, laid out in rows: choices side by side, short entries (times, numbers)
 * four to a row, and the "Given" lists together. Long entries and checklists take the full width.
 */
function SectionFields({ fields, c, data, onChange }: { fields: ChartField[]; c: ChartView; data: ChartData; onChange: (key: string, v: ChartData[string]) => void }) {
  type Group = { kind: "one"; f: ChartField } | { kind: "choices" | "inputs"; fs: ChartField[] } | { kind: "catalog"; fs: CatalogChartField[] };
  const groups: Group[] = [];
  for (const f of fields) {
    const kind = f.kind === "catalog" ? "catalog" : f.kind === "choice" ? "choices" : f.kind === "input" && !f.multiline ? "inputs" : "one";
    const last = groups[groups.length - 1];
    if (kind === "one") groups.push({ kind, f });
    else if (last && last.kind === kind) (last.fs as ChartField[]).push(f);
    else groups.push({ kind, fs: [f] } as Group);
  }
  return (
    <div className="flex flex-col gap-4">
      {groups.map((g, i) =>
        g.kind === "one" ? (
          <FieldView key={i} f={g.f} c={c} data={data} editable onChange={onChange} />
        ) : g.kind === "catalog" ? (
          <CatalogGroup key={i} fields={g.fs} c={c} data={data} editable onChange={onChange} />
        ) : (
          <div key={i} className={g.kind === "choices" ? "flex flex-wrap gap-x-8 gap-y-4" : "grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-x-4 gap-y-4"}>
            {g.fs.map((f, j) => (
              <FieldView key={j} f={f} c={c} data={data} editable onChange={onChange} />
            ))}
          </div>
        ),
      )}
    </div>
  );
}

const VITAL_SHORT: Record<VitalKey, string> = { temp: "Temp °F", pulse: "Pulse", resp: "Resp", systolic: "BP", diastolic: "BP", o2: "O₂ sat %" };
const VITAL_COLUMNS = ["bp", "pulse", "resp", "temp", "o2"] as const;
const outOfRange = (k: VitalKey, v: string | undefined) => {
  const n = Number(v);
  return v !== undefined && v !== "" && Number.isFinite(n) && (n < VITAL_RANGES[k].min || n > VITAL_RANGES[k].max);
};

/** One set of vitals on a single line (BP as systolic / diastolic in one box); out-of-range numbers turn red, never blocked. */
function VitalsRow({ f, v, editable, onChange }: { f: Extract<ChartField, { kind: "vitals" }>; v: Record<string, string>; editable: boolean; onChange: (v: Record<string, string>) => void }) {
  const lower = useRef<HTMLInputElement>(null);
  // Blood pressure typed the usual way ("119/75", or with a space or dash) splits itself: the "/" moves on to the bottom number.
  const set = (k: VitalKey, raw: string) => {
    if (k === "systolic") {
      const m = /^\s*(\d*)\s*[/\s-]\s*(\d*)\s*$/.exec(raw);
      if (m) {
        onChange({ ...v, systolic: m[1]!, diastolic: m[2] || (v.diastolic ?? "") });
        lower.current?.focus();
        return;
      }
    }
    if (k === "diastolic") raw = raw.replace(/[/\s-]/g, "");
    onChange({ ...v, [k]: raw });
  };
  const input = (k: VitalKey, cls = "") => (
    <input
      ref={k === "diastolic" ? lower : undefined}
      aria-label={`${f.label}: ${VITAL_RANGES[k].label}`}
      // The top number takes "/" (a full keyboard), so "119/75" can be typed as usual.
      inputMode={k === "systolic" ? "text" : "decimal"}
      disabled={!editable}
      className={cx("w-full min-w-0 bg-transparent px-2 py-2 text-[15px] outline-none disabled:text-ink", outOfRange(k, v[k]) && "text-danger", cls)}
      value={v[k] ?? ""}
      onChange={(e) => set(k, e.target.value)}
    />
  );
  const box = (bad: boolean) => cx("flex items-center rounded-[var(--radius-control)] border bg-white", bad ? "border-danger bg-danger-wash" : "border-line", !editable && "bg-mist");
  const out = (Object.keys(VITAL_RANGES) as VitalKey[]).filter((k) => outOfRange(k, v[k]));
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-semibold text-ink-soft">{f.label}</legend>
      <div className="grid grid-cols-[1.4fr_repeat(4,minmax(0,1fr))] gap-2">
        {VITAL_COLUMNS.map((col) => (
          <span key={col} className="text-xs font-semibold text-muted">
            {col === "bp" ? "BP" : VITAL_SHORT[col]}
          </span>
        ))}
        {VITAL_COLUMNS.map((col) =>
          col === "bp" ? (
            <div key={col} className={box(outOfRange("systolic", v.systolic) || outOfRange("diastolic", v.diastolic))}>
              {input("systolic", "text-right")}
              <span className="text-muted">/</span>
              {input("diastolic")}
            </div>
          ) : (
            <div key={col} className={box(outOfRange(col, v[col]))}>
              {input(col)}
            </div>
          ),
        )}
      </div>
      {out.length > 0 && <p className="mt-1 text-xs text-danger">Outside the usual range: {out.map((k) => `${VITAL_RANGES[k].label} ${v[k]} (${VITAL_RANGES[k].min}–${VITAL_RANGES[k].max})`).join("; ")}</p>}
    </fieldset>
  );
}

const minutesBetween = (a: string, b: string) => {
  if (!isChartTime(a) || !isChartTime(b)) return null;
  const m = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const d = m(b) - m(a);
  return d >= 0 ? d : d + 24 * 60;
};

/**
 * A signed (or read-only) chart as a document: only what was charted, values instead of buttons,
 * vitals and what was given as tables, the narrative as a paragraph, and the signatures at the end.
 */
function ChartDocument({ c, data }: { c: ChartView; data: ChartData }) {
  const all = c.template.sections.flatMap((s) => s.fields);
  const vitals = all.filter((f): f is Extract<ChartField, { kind: "vitals" }> => f.kind === "vitals");
  const catalogs = all.filter((f): f is CatalogChartField => f.kind === "catalog");
  const blocks: { title: string; body: ReactNode }[] = [];
  let vitalsShown = false;
  let givenShown = false;
  for (const s of c.template.sections) {
    const parts: ReactNode[] = [];
    const pairs: { label: string; value: string }[] = [];
    const flushPairs = () => {
      if (!pairs.length) return;
      parts.push(
        <dl key={`p${parts.length}`} className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          {pairs.splice(0).map((p) => (
            <div key={p.label}>
              <dt className="text-xs text-muted">{p.label}</dt>
              <dd className="font-semibold">{p.value}</dd>
            </div>
          ))}
        </dl>,
      );
    };
    for (const f of s.fields) {
      const v = data[f.key];
      if (f.kind === "vitals") {
        if (!vitalsShown) {
          flushPairs();
          vitalsShown = true;
          if (parts.length) blocks.push({ title: s.title, body: parts.splice(0) });
          blocks.push({ title: "Vitals", body: <VitalsTable fields={vitals} data={data} /> });
        }
        continue;
      }
      if (f.kind === "catalog") {
        if (!givenShown) {
          flushPairs();
          givenShown = true;
          if (parts.length) blocks.push({ title: s.title, body: parts.splice(0) });
          blocks.push({ title: "Given", body: <GivenTable fields={catalogs} data={data} /> });
        }
        continue;
      }
      if (f.kind === "record") {
        flushPairs();
        parts.push(<RecordBlock key={f.key} which={f.key} c={c} />);
      } else if (f.kind === "checks") {
        const picked = Array.isArray(v) ? (v as string[]) : [];
        if (!picked.length) continue;
        flushPairs();
        parts.push(
          <ul key={f.key} className="flex flex-wrap gap-x-5 gap-y-1">
            {picked.map((x) => (
              <li key={x}>
                <span className="font-bold text-ok">✓</span> {x}
              </li>
            ))}
          </ul>,
        );
      } else if (f.kind === "input" && f.multiline) {
        if (typeof v !== "string" || !v.trim()) continue;
        flushPairs();
        parts.push(
          <div key={f.key}>
            <div className="text-xs text-muted">{f.label}</div>
            <p className="whitespace-pre-wrap leading-relaxed">{v}</p>
          </div>,
        );
      } else if (typeof v === "string" && v.trim()) {
        const mins = f.key === "timeOut" && typeof data.timeIn === "string" ? minutesBetween(data.timeIn, v) : null;
        pairs.push({ label: f.label.replace(/^Time of insertion$/, "Inserted").replace(/^Time removed$/, "Removed"), value: mins !== null ? `${v} (${mins} min)` : v });
      }
    }
    flushPairs();
    if (parts.length) blocks.push({ title: s.title, body: parts });
  }
  return (
    <Panel className="overflow-hidden p-0">
      {blocks.map((b, i) => (
        <section key={i} className="flex flex-col gap-3 border-t border-line px-6 py-4 first:border-t-0">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[.12em] text-muted">{b.title}</h2>
          {b.body}
        </section>
      ))}
      <section className="flex flex-col gap-3 border-t border-line bg-mist/40 px-6 py-4">
        <h2 className="text-[11px] font-extrabold uppercase tracking-[.12em] text-muted">Signatures</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            {c.signature ? (
              <SignatureBlock title="Charted and signed by" sig={c.signature} />
            ) : c.status === "signed" ? (
              <p className="text-sm">
                Signed by <span className="font-semibold">{c.signedBy}</span> on {formatDateTime24(c.signedAt)}.
              </p>
            ) : (
              <p className="text-sm text-muted">Not signed yet.</p>
            )}
          </div>
          <div>
            {c.review.status === "reviewed" ? (
              c.review.signature ? (
                <SignatureBlock title="Reviewed by the medical director" sig={c.review.signature} />
              ) : (
                <p className="text-sm">
                  Reviewed by the medical director: <span className="font-semibold">{c.review.by}</span>, {formatDateTime24(c.review.at)}
                  {c.review.note && <span className="mt-1 block whitespace-pre-wrap text-ink-soft">{c.review.note}</span>}
                </p>
              )
            ) : c.review.status === "waiting" ? (
              <p className="text-sm text-muted">Waiting for medical director review.</p>
            ) : null}
            {c.review.status === "reviewed" && c.review.signature && c.review.note && <p className="mt-2 whitespace-pre-wrap text-sm text-ink-soft">{c.review.note}</p>}
          </div>
        </div>
      </section>
    </Panel>
  );
}

/** Vitals as a table: one row per set (before, after), headings over their columns. */
function VitalsTable({ fields, data }: { fields: Extract<ChartField, { kind: "vitals" }>[]; data: ChartData }) {
  const rows = fields.map((f) => ({ f, v: (data[f.key] ?? {}) as Record<string, string> })).filter((r) => Object.values(r.v).some((x) => x));
  if (!rows.length) return <p className="text-sm text-muted">Not taken.</p>;
  const cell = (k: VitalKey, v: Record<string, string>, suffix = "") => (v[k] ? <span className={outOfRange(k, v[k]) ? "text-danger" : ""}>{`${v[k]}${suffix}`}</span> : <span className="text-muted">—</span>);
  return (
    <table className="w-full table-fixed text-left text-[15px]">
      <thead>
        <tr className="text-[11px] font-bold uppercase tracking-wider text-muted">
          <th className="w-[22%] pb-1.5"></th>
          <th className="pb-1.5">BP</th>
          <th className="pb-1.5">Pulse</th>
          <th className="pb-1.5">Resp</th>
          <th className="pb-1.5">Temp</th>
          <th className="pb-1.5">O₂ sat</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ f, v }) => (
          <tr key={f.key} className="border-t border-mist font-semibold tabular-nums">
            <td className="py-1.5 text-sm font-semibold text-ink-soft">{f.label}</td>
            <td className="py-1.5">
              {v.systolic || v.diastolic ? (
                <span className={outOfRange("systolic", v.systolic) || outOfRange("diastolic", v.diastolic) ? "text-danger" : ""}>
                  {v.systolic || "—"}/{v.diastolic || "—"}
                </span>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
            <td className="py-1.5">{cell("pulse", v)}</td>
            <td className="py-1.5">{cell("resp", v)}</td>
            <td className="py-1.5">{cell("temp", v, " °F")}</td>
            <td className="py-1.5">{cell("o2", v, "%")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Everything given, in one table: item (and its list), each medication or bag, dose, lot and expiration. */
function GivenTable({ fields, data }: { fields: CatalogChartField[]; data: ChartData }) {
  const items = fields.flatMap((f) => ((data[f.key] as ChartItem[] | undefined) ?? []).map((x) => ({ f, x })));
  if (!items.length) return <p className="text-sm text-muted">Nothing charted.</p>;
  return (
    <table className="w-full table-fixed text-left text-[15px]">
      <thead>
        <tr className="text-[11px] font-bold uppercase tracking-wider text-muted">
          <th className="w-[28%] pb-1.5">Item</th>
          <th className="w-[32%] pb-1.5">Medication</th>
          <th className="w-[15%] pb-1.5">Dose</th>
          <th className="pb-1.5">Lot · Exp</th>
        </tr>
      </thead>
      <tbody>
        {items.flatMap(({ f, x }, n) => {
          const lines = x.ingredients?.length
            ? x.ingredients.map((g) => ({ name: g.name, dose: doseText(g), lot: g.lot || g.expiration ? `${g.lot || "—"}${g.expiration ? ` · ${fmtExp(g.expiration)}` : ""}` : "" }))
            : [{ name: f.sections.every((s) => s === "recovery") ? "Recovery" : x.name, dose: "—", lot: x.lot || x.expiration ? `${x.lot || "—"}${x.expiration ? ` · ${fmtExp(x.expiration)}` : ""}` : f.sections.every((s) => s === "recovery") ? "—" : "" }];
          return lines.map((l, k) => (
            <tr key={`${n}-${k}`} className={cx("align-top", k === 0 && "border-t border-line")}>
              {k === 0 && (
                <td rowSpan={lines.length} className="py-1.5 pr-3">
                  <div className="font-bold">{x.name}</div>
                  <div className="text-xs text-muted">
                    {f.label}
                    {x.added ? (x.atCheckout ? " · added at checkout" : " · added during the visit") : ""}
                  </div>
                </td>
              )}
              <td className="border-t border-mist py-1.5 pr-3">{l.name}</td>
              <td className="border-t border-mist py-1.5 pr-3 tabular-nums">{l.dose}</td>
              <td className={cx("border-t border-mist py-1.5", !l.lot && "text-muted")}>{l.lot || "No lot recorded"}</td>
            </tr>
          ));
        })}
      </tbody>
    </table>
  );
}
